import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Mail, ArrowRight, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Landing page after Stripe Checkout for partner bundle purchases.
 *
 * Polls `GET /partners/checkout/status/{session_id}` until the transaction
 * flips to `paid`. On paid, we already provisioned the partner user
 * account server-side — this page just tells them to check their email for
 * the set-password link.
 */
export default function PartnerCheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  // Paystack appends both `reference` and `trxref` to the callback URL.
  const paystackRef = params.get("reference") || params.get("trxref");
  const [state, setState] = useState({ loading: true, data: null, err: null });
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId && !paystackRef) {
      setState({ loading: false, data: null, err: "Missing payment reference." });
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      setPollCount(tries);
      try {
        let paid = false;
        let normalized = null;
        if (paystackRef) {
          // Paystack: single-shot verify.
          const r = await axios.get(`${API}/paystack/verify/${paystackRef}`);
          if (cancelled) return;
          paid = r.data?.payment_status === "paid";
          // On paid, hydrate the partner-side data (bundle name, qty, email) from
          // our partner_transactions row so the success screen shows the same
          // detail we render for Stripe.
          if (paid) {
            try {
              const tx = await axios.get(`${API}/partners/checkout/status/ps_${paystackRef}`);
              normalized = tx.data;
            } catch (_e) {
              normalized = { partner_email: "", bundle_name: "your bundle",
                             qty: 0, payment_status: "paid" };
            }
          } else {
            normalized = r.data;
          }
        } else {
          // Stripe: existing status endpoint (also runs fulfilment on paid).
          const r = await axios.get(`${API}/partners/checkout/status/${sessionId}`);
          if (cancelled) return;
          paid = r.data?.payment_status === "paid";
          normalized = r.data;
        }
        if (paid) {
          setState({ loading: false, data: normalized, err: null });
          return;
        }
        if (tries < 12) {
          setTimeout(poll, 2000);
        } else {
          setState({
            loading: false, data: normalized,
            err: "Payment is taking longer than expected to confirm. Please check your email in a minute — we email you as soon as we see the payment land.",
          });
        }
      } catch (e) {
        if (!cancelled) {
          setState({ loading: false, data: null, err: e?.response?.data?.detail || e.message });
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, paystackRef]);

  const paid = state.data?.payment_status === "paid";

  return (
    <div className="min-h-screen bg-cream-100 flex items-center justify-center px-4 py-16">
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 md:p-10 text-center"
        data-testid="partner-checkout-success"
      >
        {state.loading && (
          <>
            <Loader2 className="w-12 h-12 text-coral-300 animate-spin mx-auto" />
            <h1 className="font-serif text-navy-700 text-2xl mt-4">Confirming your payment…</h1>
            <p className="text-navy-700/60 text-sm mt-2">
              This usually takes a couple of seconds. (Attempt {pollCount} of 12)
            </p>
          </>
        )}

        {!state.loading && paid && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h1 className="font-serif text-navy-700 text-3xl md:text-4xl mt-4">
              You&apos;re a GalerieCop Partner
            </h1>
            <p className="text-navy-700/70 mt-3">
              Thanks for grabbing the <strong>{state.data.bundle_name}</strong> —
              <strong> {state.data.qty} event credits</strong> are already sitting in your account.
            </p>

            <div className="mt-6 bg-cream-100 border border-cream-200 rounded-2xl p-5 text-left">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-coral-400 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-navy-700 text-sm">Check your inbox</div>
                  <div className="text-navy-700/70 text-sm mt-1">
                    We emailed <strong>{state.data.partner_email}</strong> a link to set your password
                    and sign in. Once you&apos;re in, create an event and your credit will auto-apply.
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <Link
                to="/login"
                data-testid="success-goto-login"
                className="bg-coral-300 hover:bg-coral-400 text-white font-semibold py-3 rounded-full inline-flex items-center justify-center gap-2"
              >
                Sign in <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/vendor-partners"
                className="text-navy-700/70 hover:text-navy-700 text-sm underline"
              >
                Back to Partners
              </Link>
            </div>
          </>
        )}

        {!state.loading && !paid && (
          <>
            <XCircle className="w-14 h-14 text-navy-700/40 mx-auto" />
            <h1 className="font-serif text-navy-700 text-2xl mt-4">
              We couldn&apos;t confirm your payment
            </h1>
            <p className="text-navy-700/60 text-sm mt-3">
              {state.err || "The payment didn't come through. You have not been charged."}
            </p>
            <Link
              to="/vendor-partners"
              className="mt-6 inline-block bg-coral-300 hover:bg-coral-400 text-white font-semibold px-6 py-3 rounded-full"
            >
              Back to Partners
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
